// Quick office actions from the main screen (SPEC §11 as built): a new floor, a new department on
// the current floor, a new agent in a department, and renaming a floor, a department or an agent.
// Small modals over the same endpoints as the administration (POST /api/floors,
// POST /api/floors/:id/departments, POST /api/agents, PATCH /api/floors|departments|agents/:id).
// The store is patched at once (the new floor can be shown right away) and the bootstrap is
// refreshed behind. Levels: floors and departments are admin work; an agent needs `manage` on
// its department (the server enforces both again).
import { h, button, input, field, select } from '../dom.js';
import { t, registerStrings } from '../i18n/fr.js';
import { floorDepartments, deptAgents, can, floorHash, studioHash } from '../store.js';
import {
  validateFloor, validateDept, validateAgent, officeWith, unwrapItem, COLORS, LIMITS, AGENT_ENGINES,
} from './admin/model.js';
import { countedTextarea } from './admin/common.js';
import { studioDept, studioDepts } from './studio/model.js';
import './admin/strings.js';

registerStrings({
  'oa.floor.new': 'NOUVEL ÉTAGE',
  'oa.floor.newTitle': 'Nouvel étage',
  'oa.floor.newHint': 'Un étage est un projet à part : ses services, ses agents, ses connecteurs. Vous pourrez tout régler ensuite dans Administration → Étages & services.',
  'oa.floor.name': 'Nom de l’étage',
  'oa.floor.namePlaceholder': 'ex. Ecom US',
  'oa.floor.create': 'CRÉER L’ÉTAGE',
  'oa.floor.created': 'Étage « {name} » créé. Ajoutez-lui un service.',
  'oa.floor.rename': 'Renommer l’étage',
  'oa.floor.renameTitle': 'Renommer l’étage « {name} »',
  'oa.floor.renamed': 'Étage renommé « {name} ».',
  'oa.floor.menu': 'Actions sur l’étage {name}',
  'oa.floor.newItem': 'Nouvel étage',
  'oa.building.menu': 'Actions sur l’immeuble',
  'oa.floor.admin': 'Étages & services (administration)',
  'oa.dept.new': 'NOUVEAU SERVICE',
  'oa.dept.newItem': 'Nouveau service',
  'oa.dept.newTitle': 'Nouveau service',
  'oa.dept.newSubtitle': 'Sur l’étage « {floor} »',
  'oa.dept.name': 'Nom du service',
  'oa.dept.namePlaceholder': 'ex. Marketing',
  'oa.dept.create': 'CRÉER LE SERVICE',
  'oa.dept.created': 'Service « {name} » créé. Ajoutez-lui un agent.',
  'oa.dept.rename': 'Renommer le service',
  'oa.dept.renameTitle': 'Renommer le service « {name} »',
  'oa.dept.renamed': 'Service renommé « {name} ».',
  'oa.dept.menu': 'Actions sur le service {name}',
  'oa.dept.menuAria': 'Actions sur le service {name} : ajouter un agent, renommer',
  'oa.dept.edit': 'Modifier le service (administration)',
  'oa.agent.new': 'Ajouter un agent',
  'oa.agent.newShort': '+ AGENT',
  'oa.agent.newTitle': 'Nouvel agent',
  'oa.agent.newSubtitle': 'Service « {dept} » · étage « {floor} »',
  'oa.agent.create': 'CRÉER L’AGENT',
  'oa.agent.created': '{name} a rejoint le service « {dept} ».',
  'oa.agent.openSettings': 'Régler',
  'oa.agent.rename': 'Renommer l’agent',
  'oa.agent.renameHint': 'Renommer l’agent : ouvre ses Réglages sur le champ Nom',
  'oa.agent.renameTitle': 'Renommer {name}',
  'oa.agent.renamed': 'Agent renommé {name}.',
  'oa.agent.engineHint': 'Claude pour rédiger, chercher et agir avec les connecteurs ; Codex (ChatGPT) pour créer des images ; Gemini pour analyser des vidéos, des images ou des documents.',
  'oa.name': 'Nouveau nom',
  'oa.rename': 'RENOMMER',
  'oa.forbidden': 'Réservé à l’administrateur.',
  'oa.floor.studio': 'Studio de l’étage',
  'oa.dept.studio': 'Studio du service',
});

const enc = encodeURIComponent;

/** First palette color not used by `items`, else the first one (pure). */
export function freeColor(items) {
  const used = new Set((items || []).map((x) => x?.color));
  return COLORS.find((c) => !used.has(c)) || COLORS[0];
}

/**
 * Which quick actions a person may take on a floor / department (pure, mirrors SPEC §8; the
 * server decides again).
 * @param {object} state the store state
 * @param {{floorId?: string, deptId?: string}} [scope]
 * @returns {{floors: boolean, depts: boolean, agents: boolean}} create/rename floors, create/rename departments, add/rename agents
 */
export function officePermissions(state, { floorId, deptId } = {}) {
  const admin = state?.me?.role === 'admin';
  const agents = !!deptId && !!floorId && can(state, 'manage', { floorId, deptId });
  return { floors: admin, depts: admin, agents };
}

function busy(btn, on) {
  btn.disabled = on;
  btn.classList.toggle('is-busy', on);
}

/** A small form modal shared by the dialogs: fields, a submit button, one error line. */
function openForm(ctx, { title, subtitle, submitLabel, fields, onSubmit, initialFocus }) {
  const error = h('p', { class: 'field-error oa-error', role: 'alert', hidden: true });
  const setError = (msg) => { error.textContent = msg || ''; error.hidden = !msg; };
  let layer = null;
  let running = false;
  // The submit button lives in the modal footer, outside the form: it submits explicitly.
  async function submitForm() {
    if (running) return;
    running = true;
    setError(null);
    busy(submit, true);
    try {
      const ok = await onSubmit({ setError });
      if (ok) layer?.close('confirm');
    } catch (err) {
      setError(err?.message || t('common.error'));
    } finally {
      running = false;
      busy(submit, false);
    }
  }
  const submit = button(submitLabel, { variant: 'primary', onClick: () => submitForm() });
  const form = h('form', {
    class: 'stack oa-form', novalidate: true,
    onSubmit: (e) => { e.preventDefault(); submitForm(); },
  }, ...fields, error);
  const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });
  layer = ctx.overlay.openModal({ title, eyebrow: subtitle, size: 'sm', className: 'oa-modal', content: form, footer: [cancel, submit], initialFocus });
  form.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target?.tagName !== 'TEXTAREA') { e.preventDefault(); submitForm(); } });
  return layer;
}

function patchOffice(ctx, kind, item) {
  ctx.store.set({ office: officeWith(ctx.store.get().office, kind, item) });
  ctx.refresh?.office?.();
}

/**
 * Create a floor (admin): name only, the color is picked from the palette; the new floor is
 * shown at once. Everything else (description, context, connectors) is in the administration.
 * @param {object} ctx
 * @returns {import('./overlay.js').Layer|null}
 */
export function openNewFloorDialog(ctx) {
  const state = ctx.store.get();
  if (!officePermissions(state).floors) { ctx.toast?.warn?.(t('oa.forbidden')); return null; }
  const nameInput = input({ value: '', maxLength: LIMITS.floorName, placeholder: t('oa.floor.namePlaceholder'), autocomplete: 'off', autofocus: true });
  const fName = field(t('oa.floor.name'), nameInput, { hint: t('oa.floor.newHint') });
  return openForm(ctx, {
    title: t('oa.floor.newTitle'), submitLabel: t('oa.floor.create'), fields: [fName], initialFocus: nameInput,
    onSubmit: async ({ setError }) => {
      const res = validateFloor({ name: nameInput.value, color: freeColor(ctx.store.get().office.floors) });
      fName.setError(res.errors.name || null);
      if (res.errors.name) return false;
      if (!res.ok) { setError(Object.values(res.errors)[0] || t('common.error')); return false; }
      const out = await ctx.api.post('/api/floors', { name: res.value.name, color: res.value.color });
      const floor = unwrapItem(out, 'floor');
      if (floor) patchOffice(ctx, 'floors', { level: 'admin', ...floor });
      ctx.toast?.success?.(t('oa.floor.created', { name: res.value.name }), floor ? {
        action: { label: t('oa.dept.new'), onClick: () => openNewDeptDialog(ctx, floor.id) },
      } : undefined);
      if (floor) ctx.navigate(floorHash(ctx.store.get().office.floors, floor.id));
      return true;
    },
  });
}

/**
 * Create a department on a floor (admin).
 * @param {object} ctx
 * @param {string} floorId
 * @returns {import('./overlay.js').Layer|null}
 */
export function openNewDeptDialog(ctx, floorId) {
  const state = ctx.store.get();
  const floor = state.office.floors.find((f) => f.id === floorId);
  if (!floor || !officePermissions(state).depts) { ctx.toast?.warn?.(t('oa.forbidden')); return null; }
  const nameInput = input({ value: '', maxLength: LIMITS.deptName, placeholder: t('oa.dept.namePlaceholder'), autocomplete: 'off', autofocus: true });
  const fName = field(t('oa.dept.name'), nameInput);
  return openForm(ctx, {
    title: t('oa.dept.newTitle'), subtitle: t('oa.dept.newSubtitle', { floor: floor.name }), submitLabel: t('oa.dept.create'),
    fields: [fName], initialFocus: nameInput,
    onSubmit: async ({ setError }) => {
      const res = validateDept({ name: nameInput.value, color: freeColor(floorDepartments(ctx.store.get().office.departments, floorId)) });
      fName.setError(res.errors.name || null);
      if (res.errors.name) return false;
      if (!res.ok) { setError(Object.values(res.errors)[0] || t('common.error')); return false; }
      const out = await ctx.api.post(`/api/floors/${enc(floorId)}/departments`, { name: res.value.name, color: res.value.color });
      const dept = unwrapItem(out, 'department', 'dept');
      if (dept) patchOffice(ctx, 'departments', { level: 'admin', floorId, ...dept });
      ctx.toast?.success?.(t('oa.dept.created', { name: res.value.name }), dept ? {
        action: { label: t('oa.agent.new'), onClick: () => openNewAgentDialog(ctx, dept.id) },
      } : undefined);
      if (ctx.store.get().ui.floorId !== floorId) ctx.navigate(floorHash(ctx.store.get().office.floors, floorId));
      return true;
    },
  });
}

/**
 * Create an agent in a department (manage on the department): name, role, what it does, engine.
 * @param {object} ctx
 * @param {string} deptId
 * @returns {import('./overlay.js').Layer|null}
 */
export function openNewAgentDialog(ctx, deptId) {
  const state = ctx.store.get();
  const dept = state.office.departments.find((d) => d.id === deptId);
  const floor = dept ? state.office.floors.find((f) => f.id === dept.floorId) : null;
  if (!dept || !officePermissions(state, { floorId: dept.floorId, deptId }).agents) { ctx.toast?.warn?.(t('ad.settings.forbidden')); return null; }
  const nameInput = input({
    value: '', maxLength: LIMITS.agentName, autocomplete: 'off', autofocus: true, class: 'adm-upper',
    onBlur: () => { nameInput.value = nameInput.value.toUpperCase(); },
  });
  const fName = field(t('adm.agent.name'), nameInput, { hint: t('adm.agent.nameHint') });
  const roleInput = input({ value: '', maxLength: LIMITS.agentRole, placeholder: t('adm.agent.rolePlaceholder'), autocomplete: 'off' });
  const fRole = field(t('adm.agent.role'), roleInput);
  const doesArea = countedTextarea({ max: LIMITS.agentDoes, rows: 3 });
  const fDoes = field(t('adm.agent.does'), doesArea, { hint: t('adm.agent.doesHint'), optional: true });
  const engineSel = select(AGENT_ENGINES.map((e) => ({ value: e, label: t(`adm.agent.engine.${e}`) })), { value: 'claude' });
  const fEngine = field(t('adm.agent.engine'), engineSel, { hint: t('oa.agent.engineHint') });
  return openForm(ctx, {
    title: t('oa.agent.newTitle'), subtitle: t('oa.agent.newSubtitle', { dept: dept.name, floor: floor?.name || '' }),
    submitLabel: t('oa.agent.create'), fields: [fName, fRole, fDoes, fEngine], initialFocus: nameInput,
    onSubmit: async ({ setError }) => {
      const res = validateAgent({ name: nameInput.value, role: roleInput.value, does: doesArea.input.value, engine: engineSel.value });
      fName.setError(res.errors.name || null);
      fRole.setError(res.errors.role || null);
      fDoes.setError(res.errors.does || null);
      if (!res.ok) { setError(t('adm.formError')); return false; }
      const out = await ctx.api.post('/api/agents', { deptId, ...res.value });
      const agent = unwrapItem(out, 'agent');
      if (agent) patchOffice(ctx, 'agents', { floorId: dept.floorId, deptId, ...agent });
      ctx.toast?.success?.(t('oa.agent.created', { name: res.value.name, dept: dept.name }), agent ? {
        action: { label: t('oa.agent.openSettings'), onClick: () => ctx.navigate(`#/agent/${enc(agent.id)}/settings`) },
      } : undefined);
      return true;
    },
  });
}

const RENAME = {
  floor: { list: 'floors', path: (id) => `/api/floors/${enc(id)}`, max: LIMITS.floorName, title: 'oa.floor.renameTitle', done: 'oa.floor.renamed', unwrap: ['floor'] },
  dept: { list: 'departments', path: (id) => `/api/departments/${enc(id)}`, max: LIMITS.deptName, title: 'oa.dept.renameTitle', done: 'oa.dept.renamed', unwrap: ['department', 'dept'] },
  agent: { list: 'agents', path: (id) => `/api/agents/${enc(id)}`, max: LIMITS.agentName, title: 'oa.agent.renameTitle', done: 'oa.agent.renamed', unwrap: ['agent'] },
};

/**
 * Rename a floor, a department (admin) or an agent (manage): one prompt, then PATCH {name}.
 * @param {object} ctx
 * @param {'floor'|'dept'|'agent'} kind
 * @param {string} id
 * @returns {Promise<boolean>} true when renamed
 */
export async function renameItem(ctx, kind, id) {
  const spec = RENAME[kind];
  const state = ctx.store.get();
  const item = spec ? (state.office[spec.list] || []).find((x) => x.id === id) : null;
  if (!item) return false;
  const perms = officePermissions(state, { floorId: item.floorId, deptId: kind === 'agent' ? item.deptId : undefined });
  const allowed = kind === 'agent' ? perms.agents : perms.floors;
  if (!allowed) { ctx.toast?.warn?.(kind === 'agent' ? t('ad.settings.forbidden') : t('oa.forbidden')); return false; }
  const value = await ctx.overlay.prompt({
    title: t(spec.title, { name: item.name }), label: t('oa.name'), value: item.name, maxLength: spec.max, confirmLabel: t('oa.rename'),
    hint: kind === 'agent' ? t('adm.agent.nameHint') : undefined,
  });
  if (value === null) return false;
  let name = String(value).replace(/\s+/g, ' ').trim();
  if (kind === 'agent') name = name.toUpperCase();
  if (!name || name === item.name) return false;
  try {
    const out = await ctx.api.patch(spec.path(id), { name });
    const updated = unwrapItem(out, ...spec.unwrap) || { id, name };
    patchOffice(ctx, spec.list, { ...item, ...updated });
    ctx.toast?.success?.(t(spec.done, { name: updated.name || name }));
    return true;
  } catch (err) {
    ctx.toast?.error?.(err);
    return false;
  }
}

/**
 * The actions menu of a floor (top bar): rename, new department, new floor, administration. The
 * items share one case (« Renommer l’étage », « Nouveau service », « Nouvel étage »).
 * @param {object} ctx
 * @param {HTMLElement} anchor
 * @param {string} floorId
 */
export function openFloorMenu(ctx, anchor, floorId) {
  const state = ctx.store.get();
  const floor = state.office.floors.find((f) => f.id === floorId);
  if (!floor) return null;
  // Everyone who sees a service of a floor with a studio gets « Studio de l’étage »; the rest is for admins.
  const studio = studioDepts(state.office, floorId).length
    ? [{ label: t('oa.floor.studio'), icon: 'film', onSelect: () => ctx.navigate(studioHash(state.office.floors, floorId)) }]
    : [];
  if (!officePermissions(state).floors) {
    return studio.length ? ctx.overlay.menu(anchor, studio, { placement: 'bottom-start', label: t('oa.floor.menu', { name: floor.name }) }) : null;
  }
  return ctx.overlay.menu(anchor, [
    ...studio,
    ...(studio.length ? [{ separator: true }] : []),
    { label: t('oa.floor.rename'), icon: 'pencil', onSelect: () => renameItem(ctx, 'floor', floorId) },
    { label: t('oa.dept.newItem'), icon: 'plus', onSelect: () => openNewDeptDialog(ctx, floorId) },
    { separator: true },
    { label: t('oa.floor.newItem'), icon: 'building', onSelect: () => openNewFloorDialog(ctx) },
    { label: t('oa.floor.admin'), icon: 'settings', onSelect: () => ctx.navigate('#/admin/floors') },
  ], { placement: 'bottom-start', label: t('oa.floor.menu', { name: floor.name }) });
}

/**
 * The actions menu of the building view (top bar, admins): a new floor, administration. The floor
 * menu is not there in this view (no current floor).
 * @param {object} ctx
 * @param {HTMLElement} anchor
 */
export function openBuildingMenu(ctx, anchor) {
  if (!officePermissions(ctx.store.get()).floors) return null;
  return ctx.overlay.menu(anchor, [
    { label: t('oa.floor.newItem'), icon: 'building', onSelect: () => openNewFloorDialog(ctx) },
    { label: t('oa.floor.admin'), icon: 'settings', onSelect: () => ctx.navigate('#/admin/floors') },
  ], { placement: 'bottom-start', label: t('oa.building.menu') });
}

/**
 * The actions menu of a department (card): add an agent (manage), rename and edit (admin).
 * @param {object} ctx
 * @param {HTMLElement} anchor
 * @param {string} deptId
 */
export function openDeptMenu(ctx, anchor, deptId) {
  const state = ctx.store.get();
  const dept = state.office.departments.find((d) => d.id === deptId);
  if (!dept) return null;
  const perms = officePermissions(state, { floorId: dept.floorId, deptId });
  if (!perms.agents && !perms.depts) return null;
  const items = [];
  if (perms.agents) items.push({ label: t('oa.agent.new'), icon: 'plus', onSelect: () => openNewAgentDialog(ctx, deptId), hint: String(deptAgents(state.office.agents, deptId).length) });
  if (studioDept(state.office, deptId)) {
    items.push({ label: t('oa.dept.studio'), icon: 'film', onSelect: () => ctx.navigate(studioHash(state.office.floors, dept.floorId, deptId)) });
  }
  if (perms.depts) {
    items.push({ label: t('oa.dept.rename'), icon: 'pencil', onSelect: () => renameItem(ctx, 'dept', deptId) });
    items.push({ label: t('oa.dept.edit'), icon: 'settings', onSelect: () => ctx.navigate('#/admin/floors') });
  }
  return ctx.overlay.menu(anchor, items, { placement: 'bottom-end', label: t('oa.dept.menu', { name: dept.name }) });
}

/**
 * The floors a person may pick as "the next one" in the elevator (pure helper for the button).
 * @param {object} state
 */
export function canCreateFloor(state) {
  return officePermissions(state).floors;
}
